/*
 * Collection card parking (iOS memory fix)
 *
 * iOS Safari kills the WebContent process on /collections/all-products because the
 * whole catalog renders into one DOM (paginate by 1000, deliberate "show all" look):
 * every card keeps its decoded bitmaps and its own compositor layer alive. Instead of
 * paginating, this script "parks" grid items far outside the viewport: the item keeps
 * its exact rendered height as an inline min-height (so layout and scroll position
 * never move) while its content is display:none — which lets Safari drop both the
 * backing stores and the decoded images. Items are restored well before they scroll
 * back into view, so the swap is invisible. Memory stays bounded regardless of
 * catalog size; without JS the page simply behaves as before.
 */
(() => {
  if (window.Shopify && Shopify.designMode) return;
  if (!('IntersectionObserver' in window)) return;

  // Keep ~3 viewport heights above and below the viewport live.
  const ROOT_MARGIN = '300% 0px 300% 0px';
  let observer = null;

  const park = (item) => {
    if (item.dataset.parked === 'true') return;
    // Never park a card the user is interacting with (open modal/details, focus).
    if (item.querySelector('[open]')) return;
    if (item.contains(document.activeElement)) return;
    const height = item.offsetHeight;
    if (!height) return;
    item.style.minHeight = `${height}px`;
    item.dataset.parked = 'true';
    for (const child of item.children) child.style.display = 'none';
  };

  const unpark = (item) => {
    if (item.dataset.parked !== 'true') return;
    item.dataset.parked = 'false';
    for (const child of item.children) child.style.display = '';
    item.style.minHeight = '';
  };

  const init = () => {
    if (observer) observer.disconnect();
    const grid = document.getElementById('product-grid');
    if (!grid) return;
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            unpark(entry.target);
          } else {
            park(entry.target);
          }
        }
      },
      { rootMargin: ROOT_MARGIN }
    );
    grid.querySelectorAll(':scope > .grid__item').forEach((item) => observer.observe(item));
  };

  // Parked min-heights go stale when the layout reflows (orientation change,
  // desktop resize) — release everything and let the observer re-park.
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      document
        .querySelectorAll('#product-grid > .grid__item[data-parked="true"]')
        .forEach(unpark);
    }, 250);
  });

  // facets.js replaces ProductGridContainer.innerHTML on filter/sort — re-attach.
  const attachGridWatcher = () => {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    new MutationObserver(() => init()).observe(container, { childList: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      attachGridWatcher();
    });
  } else {
    init();
    attachGridWatcher();
  }
})();
